/**
 * AI Flags Panel – enterprise redesign with severity borders and clear actions
 */
import { cn } from '@respectly/ui';
import { AlertTriangle, FileText, MessageSquare, FormInput, CheckCircle2, X, Eye } from 'lucide-react';
import type { AIFlaggedContent, FlagSeverity, FlaggedContentType } from '@respectly/shared-types';
import { formatDistanceToNow } from 'date-fns';

interface AIFlagsPanelProps {
  flags: AIFlaggedContent[];
  isLoading?: boolean;
  onReview?: (id: string, action: 'reviewed' | 'dismissed' | 'actioned') => void;
  className?: string;
}

const severityConfig: Record<FlagSeverity, { label: string; stripe: string; bg: string; text: string; ring: string }> = {
  low: {
    label: 'Low',
    stripe: 'border-l-zinc-400',
    bg: 'bg-zinc-50 dark:bg-zinc-800/50',
    text: 'text-zinc-600 dark:text-zinc-400',
    ring: 'ring-zinc-200 dark:ring-zinc-700',
  },
  medium: {
    label: 'Medium',
    stripe: 'border-l-amber-400',
    bg: 'bg-amber-50/50 dark:bg-amber-900/10',
    text: 'text-amber-600 dark:text-amber-400',
    ring: 'ring-amber-200 dark:ring-amber-800',
  },
  high: {
    label: 'High',
    stripe: 'border-l-red-500',
    bg: 'bg-red-50/50 dark:bg-red-900/10',
    text: 'text-red-600 dark:text-red-400',
    ring: 'ring-red-200 dark:ring-red-800',
  },
};

const contentTypeIcons: Record<FlaggedContentType, typeof MessageSquare> = {
  message: MessageSquare,
  document: FileText,
  form: FormInput,
};

export function AIFlagsPanel({ flags, isLoading, onReview, className }: AIFlagsPanelProps) {
  return (
    <div className={cn('px-4 py-4', className)}>
      {/* Header */}
      <div className="flex items-center gap-2 mb-3">
        <div className="h-6 w-6 rounded-lg bg-amber-500/10 ring-1 ring-amber-500/20 flex items-center justify-center">
          <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />
        </div>
        <h3 className="text-xs font-semibold text-zinc-700 dark:text-zinc-300 uppercase tracking-wider flex-1">
          AI Flagged
        </h3>
        {!isLoading && flags.length > 0 && (
          <span className="font-mono text-[11px] px-1.5 py-0.5 rounded-md bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 ring-1 ring-red-200 dark:ring-red-800">
            {flags.length}
          </span>
        )}
      </div>

      {isLoading ? (
        <div className="space-y-3">
          <FlagSkeleton />
          <FlagSkeleton />
        </div>
      ) : flags.length === 0 ? (
        <div className="flex flex-col items-center py-8 text-center">
          <div className="h-8 w-8 rounded-full bg-emerald-100 dark:bg-emerald-900/30 ring-1 ring-emerald-200 dark:ring-emerald-800 flex items-center justify-center mb-2">
            <CheckCircle2 className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
          </div>
          <p className="text-xs font-medium text-zinc-600 dark:text-zinc-400">All clear</p>
          <p className="text-[11px] text-zinc-400 dark:text-zinc-500 mt-0.5">No pending flags</p>
        </div>
      ) : (
        <div className="space-y-2.5">
          {flags.map((flag) => (
            <FlagCard key={flag.id} flag={flag} onReview={onReview} />
          ))}
        </div>
      )}
    </div>
  );
}

// ── Flag Card ─────────────────────────────────────────────────────────────────

function FlagCard({ flag, onReview }: { flag: AIFlaggedContent; onReview?: AIFlagsPanelProps['onReview'] }) {
  const sev = severityConfig[flag.severity];
  const ContentIcon = contentTypeIcons[flag.contentType];
  const timeAgo = formatDistanceToNow(new Date(flag.createdAt), { addSuffix: true });

  return (
    <div
      className={cn(
        'rounded-xl border-l-4 p-3 ring-1',
        sev.stripe,
        sev.bg,
        sev.ring
      )}
    >
      {/* Header */}
      <div className="flex items-start gap-2 mb-2">
        <div className="h-5 w-5 rounded-md bg-white dark:bg-zinc-800 ring-1 ring-zinc-200 dark:ring-zinc-700 flex items-center justify-center shrink-0">
          <ContentIcon className={cn('h-3 w-3', sev.text)} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className={cn('text-[11px] font-semibold', sev.text)}>
              {sev.label}
            </span>
            <span className="text-[10px] text-zinc-400">·</span>
            <span className="text-[11px] text-zinc-500 dark:text-zinc-400 capitalize">
              {flag.contentType}
            </span>
          </div>
          {flag.tenant && (
            <p className="text-[11px] text-zinc-400 dark:text-zinc-500 truncate">{flag.tenant.name}</p>
          )}
        </div>
        <span className="text-[10px] text-zinc-400 dark:text-zinc-500 shrink-0">{timeAgo}</span>
      </div>

      {/* Reason */}
      <p className="text-xs text-zinc-700 dark:text-zinc-300 line-clamp-2 mb-2 leading-relaxed">
        {flag.flagReason}
      </p>

      {/* Content preview */}
      {flag.contentPreview && (
        <div className="mb-2 rounded-lg bg-white/60 dark:bg-zinc-900/60 px-2.5 py-1.5 ring-1 ring-zinc-200/60 dark:ring-zinc-700/60">
          <p className="text-[11px] text-zinc-500 dark:text-zinc-400 line-clamp-2 font-mono">
            "{flag.contentPreview}"
          </p>
        </div>
      )}

      {/* Actions */}
      {onReview && (
        <div className="flex items-center gap-1.5">
          <button
            onClick={() => onReview(flag.id, 'dismissed')}
            className="flex items-center gap-1 text-[11px] font-medium text-zinc-500 dark:text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-200 transition-colors px-2 py-1 rounded-lg hover:bg-white/60 dark:hover:bg-zinc-800/60"
          >
            <X className="h-3 w-3" />
            Dismiss
          </button>
          <button
            onClick={() => onReview(flag.id, 'reviewed')}
            className={cn(
              'flex items-center gap-1 text-[11px] font-semibold transition-colors px-2 py-1 rounded-lg',
              flag.severity === 'high'
                ? 'text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/30'
                : flag.severity === 'medium'
                ? 'text-amber-600 dark:text-amber-400 hover:bg-amber-100 dark:hover:bg-amber-900/30'
                : 'text-emerald-600 dark:text-emerald-400 hover:bg-emerald-100 dark:hover:bg-emerald-900/30'
            )}
          >
            <Eye className="h-3 w-3" />
            Review
          </button>
        </div>
      )}
    </div>
  );
}

// ── Skeleton ──────────────────────────────────────────────────────────────────

function FlagSkeleton() {
  return (
    <div className="rounded-xl border-l-4 border-l-zinc-200 dark:border-l-zinc-700 p-3 ring-1 ring-zinc-100 dark:ring-zinc-800 animate-pulse">
      <div className="flex items-start gap-2 mb-2">
        <div className="h-5 w-5 rounded-md bg-zinc-200 dark:bg-zinc-700 shrink-0" />
        <div className="flex-1 space-y-1">
          <div className="h-3 bg-zinc-200 dark:bg-zinc-700 rounded w-2/3" />
          <div className="h-2.5 bg-zinc-100 dark:bg-zinc-800 rounded w-1/3" />
        </div>
      </div>
      <div className="space-y-1">
        <div className="h-2.5 bg-zinc-100 dark:bg-zinc-800 rounded w-full" />
        <div className="h-2.5 bg-zinc-100 dark:bg-zinc-800 rounded w-3/4" />
      </div>
    </div>
  );
}
